import React, { useCallback } from 'react';
import { styled as styled2 } from '@compiled/react';
import Button from '@atlaskit/button';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { AtlassianMarketplaceLogo } from '@atlaskit/logo';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import MarketplaceAppTiles from '@atlassian/jira-marketplace-app-tiles/async';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { useCodeLocalStore } from '../../../../../controllers';
import { isEndUserDiscoverOn } from '../../../../../feature-flags';
import marketplaceLogo from './assets/marketplace-logo.svg';
import messages from './messages';
import { transformMarketplaceApps } from './utils';

type EmbeddedMarketplaceProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onRefChange: (node: any) => void;
	onRefetch: () => void;
};

export const EmbeddedMarketplace = ({ onRefChange, onRefetch }: EmbeddedMarketplaceProps) => {
	const tenantContext = useTenantContext();
	const { isSiteAdmin } = tenantContext;
	const { formatMessage } = useIntl();

	const [codeLocalState] = useCodeLocalStore();

	const refresh = useCallback(() => {
		onRefetch();
	}, [onRefetch]);

	const BROWSE_APPS_LINK =
		'https://marketplace.atlassian.com/search?category=Source%20code&hosting=cloud&product=jira';

	const marketplaceAppTilesProps = {
		isJiraAdmin: isSiteAdmin,
		marketingLabel: 'Jira-Git',
		source: 'code-in-jira',
		actionSubjectId: 'code-in-jira',
		onSortHits: transformMarketplaceApps,
		installDialogOpen: true,
		...(isEndUserDiscoverOn() && {
			projectKey: codeLocalState.projectContext.projectKey,
			projectId: codeLocalState.projectContext.projectId,
			requestDialogOpen: true,
		}),
		installInContext: true,
		onAppInstallSuccess: refresh,

		...(fg('jsw_scm_code_app_install_modal_fix_feature_gate') && {
			onAppInstallSuccess: () => {
				if (!expVal('jsw_scm_code_app_install_modal_fix', 'isEnabled', false)) {
					refresh();
				}
			},
			onDismissSuccessModal: () => {
				if (expVal('jsw_scm_code_app_install_modal_fix', 'isEnabled', false)) {
					refresh();
				}
			},
		}),
	};

	return (
		<Wrapper>
			{ff('isotopes-consolidate-jfe-assets_pddkf') ? (
				<AtlassianMarketplaceLogo appearance="brand" size="small" />
			) : (
				<LogoNoMargin src={marketplaceLogo} />
			)}
			<ContextualAnalyticsData>
				<MarketplaceAppTiles {...marketplaceAppTilesProps} />
			</ContextualAnalyticsData>
			<FooterWrapper>
				<Button
					appearance="link"
					ref={onRefChange}
					href={BROWSE_APPS_LINK}
					iconAfter={
						<ShortcutIcon label={formatMessage(messages.browseMarketplaceLinkText)} size="small" />
					}
				>
					{formatMessage(messages.browseMarketplaceLinkText)}
				</Button>
			</FooterWrapper>
		</Wrapper>
	);
};
export default EmbeddedMarketplace;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled2.div({
	height: '100%',
	width: '688px',
	padding: token('space.100', '8px'),
	margin: '0 auto',
	borderRadius: token('space.100', '8px'),
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogoNoMargin = styled2.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.brand', colors.B400),
	marginBottom: 0,
	width: '324px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterWrapper = styled2.div({
	marginTop: token('space.400', '32px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.brand', colors.B400),
	display: 'flex',
});
